import {
  View,
  Text,
  StyleSheet,
  Font,
  Document,
  Page,
  Image,
} from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { NewEssayEvaluationResult } from "@packages/api/madlenAPI.schemas";
import logo from "@packages/assets/logo.png";
import { useTranslation } from "react-i18next";
import MarkdownToPdf from "@features/tools/components/MarkdownToPdf";

// Register fonts
Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

// Define styles
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: "Montserrat",
    backgroundColor: "#ffffff",
  },
  header: {
    marginBottom: 20,
    borderBottom: "1px solid #e5e7eb",
    paddingBottom: 10,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#1f2937",
    marginBottom: 5,
  },
  scoreContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 10,
  },
  scoreLabel: {
    fontSize: 12,
    color: "#4b5563",
  },
  scoreValue: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2563eb",
    marginLeft: 5,
  },
  section: {
    marginBottom: 15,
    padding: 10,
    backgroundColor: "#f9fafb",
    borderRadius: 5,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#1f2937",
    marginBottom: 8,
  },
  sectionContent: {
    fontSize: 10,
    color: "#4b5563",
    lineHeight: 1.5,
  },
  summarySection: {
    marginBottom: 15,
    padding: 10,
    backgroundColor: "#eff6ff",
    borderRadius: 5,
  },
  logo: {
    width: 64,
    height: 64,
    marginBottom: 15,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  sectionScore: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#6366f1",
    backgroundColor: "#eef2ff",
    padding: "3 6",
    borderRadius: 10,
  },
  titleContainer: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
});

// PDF Document Component
const EssayGraderPdfDocument = ({
  message,
}: {
  message: NewEssayEvaluationResult;
}) => {
  const { t } = useTranslation();

  return (
    <Document
      title={t("assistant.essayGrader.pdfTitle")}
      author="Madlen Teacher"
      subject={t("assistant.essayGrader.pdfSubject")}
    >
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.titleContainer}>
            <Text style={styles.title}>
              {t("assistant.essayGrader.aiAnalysis")}
            </Text>

            {message.score !== undefined && message.max_score !== undefined && (
              <View style={styles.scoreContainer}>
                <Text style={styles.scoreLabel}>
                  {t("assistant.essayGrader.totalScore")}:
                </Text>
                <Text style={styles.scoreValue}>
                  {message.score}/{message.max_score}
                </Text>
              </View>
            )}
          </View>
          <Image src={logo} style={styles.logo} />
        </View>

        {/* Overall Summary */}
        {message.overall_summary && (
          <View style={styles.summarySection}>
            <Text style={styles.sectionTitle}>
              {t("assistant.essayGrader.overallSummary")}
            </Text>
            <MarkdownToPdf globalStyle={styles.sectionContent}>
              {message.overall_summary}
            </MarkdownToPdf>
          </View>
        )}

        {/* Criteria Evaluations */}
        {message.criteria_evaluations &&
          message.criteria_evaluations.length > 0 && (
            <View style={[styles.section, { marginTop: 10 }]}>
              <Text style={styles.sectionTitle}>
                {t("assistant.essayGrader.criteriaEvaluations")}
              </Text>

              {message.criteria_evaluations.map((criterion, index) => (
                <View key={index} style={{ marginBottom: 10 }}>
                  <View style={styles.sectionHeader}>
                    <Text
                      style={{ ...styles.sectionContent, fontWeight: "bold" }}
                    >
                      {criterion.criterion_name}
                    </Text>
                    {criterion.score !== undefined &&
                      criterion.max_score !== undefined && (
                        <Text style={styles.sectionScore}>
                          {t("assistant.essayGrader.score")}: {criterion.score}/
                          {criterion.max_score}
                        </Text>
                      )}
                  </View>

                  {criterion.justification_with_evidence && (
                    <MarkdownToPdf globalStyle={styles.sectionContent}>
                      {criterion.justification_with_evidence}
                    </MarkdownToPdf>
                  )}
                </View>
              ))}
            </View>
          )}
      </Page>
    </Document>
  );
};

// Export the PDF component
export default EssayGraderPdfDocument;
